import * as THREE from 'three';
import type { System } from '../../../systems';
import type { WeatherSpec } from '../BiomeProfile';

/**
 * Atmospheric particle system for planet interiors. Implements every
 * weather signature from the biome registry:
 *
 *   salt-gust   horizontal pale particles, drifting briskly
 *   spore-haze  warm green motes rising slowly
 *   rust-dust   amber dust streams, gusty
 *   heat-shimmer slow gold motes plus an additive haze
 *   crystal-storm cold lavender shards falling
 *   freezing-wind fast pale snow-like flecks
 *   photophore-pulse glowing pink motes that brighten in waves
 *   radiation-haze toxic peach particles, slow
 *   metal-dust rare silver flecks (moons)
 *
 * Particles are recycled in a cube around the player so they're always
 * visible without instantiating thousands of objects across the disc.
 *
 * Visual style is uniform across types — small additive points with a
 * soft round texture — but tints, drift, fall and density carry the
 * identity. Cheaper than per-type shaders, equally evocative.
 */

export class Weather implements System {
  readonly id = 'biome-weather';
  readonly object3D: THREE.Points;
  private readonly material: THREE.PointsMaterial;
  private readonly positions: Float32Array;
  private readonly velocities: Float32Array;
  private readonly seeds: Float32Array;
  private readonly tintBuffer: Float32Array;
  private readonly geometry: THREE.BufferGeometry;
  private readonly tintColor: THREE.Color;
  private readonly accentColor: THREE.Color;
  private readonly drift: number;
  private readonly fall: number;
  private readonly pulse: number;
  private readonly anchor: THREE.Object3D;
  private readonly range: number;
  private readonly halfRange: number;
  private time = 0;

  constructor(
    spec: WeatherSpec,
    anchor: THREE.Object3D,
    range = 220
  ) {
    this.anchor = anchor;
    this.range = range;
    this.halfRange = range / 2;
    this.tintColor = new THREE.Color(spec.tint);
    this.accentColor = new THREE.Color(spec.accent ?? spec.tint);
    this.drift = spec.drift;
    this.fall = spec.fall;
    this.pulse = spec.pulse ?? 0;

    const count = spec.density;
    this.positions = new Float32Array(count * 3);
    this.velocities = new Float32Array(count * 3);
    this.seeds = new Float32Array(count);
    this.tintBuffer = new Float32Array(count * 3);

    const driftAngle = Math.random() * Math.PI * 2;
    const driftX = Math.cos(driftAngle);
    const driftZ = Math.sin(driftAngle);

    for (let i = 0; i < count; i++) {
      this.positions[i * 3 + 0] = (Math.random() - 0.5) * range;
      this.positions[i * 3 + 1] = Math.random() * range * 0.7;
      this.positions[i * 3 + 2] = (Math.random() - 0.5) * range;

      const speedJitter = 0.7 + Math.random() * 0.6;
      this.velocities[i * 3 + 0] = driftX * this.drift * speedJitter;
      this.velocities[i * 3 + 1] = -this.fall * speedJitter;
      this.velocities[i * 3 + 2] = driftZ * this.drift * speedJitter;
      this.seeds[i] = Math.random() * Math.PI * 2;

      const mix = Math.random();
      const c = this.tintColor.clone().lerp(this.accentColor, mix);
      this.tintBuffer[i * 3 + 0] = c.r;
      this.tintBuffer[i * 3 + 1] = c.g;
      this.tintBuffer[i * 3 + 2] = c.b;
    }

    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(this.positions, 3)
    );
    this.geometry.setAttribute(
      'color',
      new THREE.BufferAttribute(this.tintBuffer, 3)
    );

    this.material = new THREE.PointsMaterial({
      size: spec.size,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      vertexColors: true,
      map: createSoftDot(),
      toneMapped: false
    });

    this.object3D = new THREE.Points(this.geometry, this.material);
    this.object3D.frustumCulled = false;
    this.object3D.name = `weather-${spec.kind}`;
  }

  update(dt: number): void {
    this.time += dt;

    const anchorPos = this.anchor.position;
    const positions = this.positions;
    const velocities = this.velocities;
    const half = this.halfRange;
    const range = this.range;
    const count = positions.length / 3;

    // Anchor the particle cube to the player but smooth follow so the
    // centre never snaps when the camera teleports.
    this.object3D.position.x = anchorPos.x;
    this.object3D.position.z = anchorPos.z;
    this.object3D.position.y = Math.max(anchorPos.y - 4, 0);

    for (let i = 0; i < count; i++) {
      const ix = i * 3;
      const iy = ix + 1;
      const iz = ix + 2;

      const seed = this.seeds[i] ?? 0;
      const swirl = Math.sin(this.time * 0.6 + seed) * 0.7;

      positions[ix] = (positions[ix] ?? 0) + (velocities[ix] ?? 0) * dt + swirl * dt;
      positions[iy] = (positions[iy] ?? 0) + (velocities[iy] ?? 0) * dt;
      positions[iz] = (positions[iz] ?? 0) + (velocities[iz] ?? 0) * dt;

      // Wrap each axis when the particle leaves the cube — recycles into
      // the opposite face so the field always surrounds the player.
      let px = positions[ix] ?? 0;
      let py = positions[iy] ?? 0;
      let pz = positions[iz] ?? 0;
      if (px > half) px -= range;
      else if (px < -half) px += range;
      if (pz > half) pz -= range;
      else if (pz < -half) pz += range;
      // Vertical recycle: floor at 0, ceiling at range*0.7.
      const yCeil = range * 0.7;
      if (py > yCeil) py -= yCeil;
      else if (py < 0) py += yCeil;
      positions[ix] = px;
      positions[iy] = py;
      positions[iz] = pz;
    }

    (this.geometry.attributes['position'] as THREE.BufferAttribute).needsUpdate =
      true;

    if (this.pulse > 0) {
      const pulse = 0.7 + Math.sin(this.time * 1.4) * 0.3 * this.pulse;
      this.material.opacity = 0.55 + pulse * 0.4;
    }
  }

  dispose(): void {
    this.geometry.dispose();
    // Don't dispose the dot texture — it's shared across all weather
    // instances and reused on every planet load.
    this.material.dispose();
  }
}

let cachedDot: THREE.Texture | null = null;
function createSoftDot(): THREE.Texture {
  if (cachedDot) return cachedDot;
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    cachedDot = new THREE.Texture();
    return cachedDot;
  }
  const gradient = ctx.createRadialGradient(
    size / 2,
    size / 2,
    0,
    size / 2,
    size / 2,
    size / 2
  );
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.45, 'rgba(255,255,255,0.55)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  const texture = new THREE.CanvasTexture(canvas);
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  cachedDot = texture;
  return texture;
}
